import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { utcToChileanInputDate, chileanInputDateToISO, getCurrentChileanDate, dateToChileanString } from '../../utils/dateUtils';

export const ProgressiveDaysDialog = ({ employee, open, onOpenChange, onUpdateEmployee }) => {
  const [startDate, setStartDate] = useState('');
  const [approvalDate, setApprovalDate] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (employee) {
      setStartDate(employee.progressiveDaysStartDate 
        ? utcToChileanInputDate(employee.progressiveDaysStartDate)
        : '');
      setApprovalDate(employee.progressiveDaysApprovalDate 
        ? utcToChileanInputDate(employee.progressiveDaysApprovalDate)
        : '');
    }
  }, [employee]);

  const calculateProgressiveDays = () => {
    if (!startDate) return 0;
    
    const now = getCurrentChileanDate();
    const start = new Date(chileanInputDateToISO(startDate));
    
    if (start > now) return 0;
    
    // Primer día progresivo inmediato
    let progressiveDays = 1;
    
    // Calcular años completos transcurridos
    const yearsDiff = now.getFullYear() - start.getFullYear();
    const monthsDiff = now.getMonth() - start.getMonth();
    const daysDiff = now.getDate() - start.getDate();
    
    let yearsCompleted = yearsDiff;
    if (monthsDiff < 0 || (monthsDiff === 0 && daysDiff < 0)) {
      yearsCompleted--;
    }
    
    // Cada 3 años completos = 1 día progresivo adicional
    const additionalDays = Math.floor(yearsCompleted / 3);
    progressiveDays += additionalDays;
    
    return progressiveDays;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!employee) {
      toast.error('No hay empleado seleccionado');
      return;
    }

    if (!startDate) {
      toast.error('La fecha de inicio es obligatoria');
      return;
    }

    try {
      const updateData = {
        progressiveDaysStartDate: chileanInputDateToISO(startDate),
        progressiveDaysApprovalDate: approvalDate ? chileanInputDateToISO(approvalDate) : null
      };

      await onUpdateEmployee?.(employee.id || employee._id, updateData);
      
      const calculatedDays = calculateProgressiveDays();
      toast.success(`Días progresivos configurados exitosamente. Días calculados: ${calculatedDays}`);
      
      onOpenChange(false);
      setReason('');
    } catch (error) {
      console.error('Error configurando días progresivos:', error);
      toast.error('Error al configurar días progresivos: ' + (error.message || 'Error desconocido'));
    }
  };

  const resetDates = () => {
    setStartDate('');
    setApprovalDate('');
    setReason('');
  };

  const handleClear = async () => {
    if (!employee) return;
    const ok = window.confirm('¿Eliminar la configuración de días progresivos? Esto pondrá en blanco las fechas y en 0 los días progresivos.');
    if (!ok) return;
    try {
      await onUpdateEmployee?.(employee.id || employee._id, {
        progressiveDaysStartDate: null,
        progressiveDaysApprovalDate: null,
        progressiveDays: 0
      });
      toast.success('Configuración de días progresivos eliminada');
      resetDates();
      onOpenChange(false);
    } catch (err) {
      console.error('Error limpiando días progresivos:', err);
      toast.error('No se pudo eliminar la configuración');
    }
  };

  const previewDays = calculateProgressiveDays();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Configurar Días Progresivos</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Configure las fechas para el cálculo automático de días progresivos. 
            El primer día se otorga inmediatamente al establecer la fecha de inicio, 
            luego se suma 1 día cada 3 años.
          </p>
        </DialogHeader>
        
        {employee && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información del empleado */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{employee.name}</p>
                  <p className="text-sm text-gray-600">{employee.position}</p>
                </div>
                {previewDays > 0 && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Días calculados:</p>
                    <p className="text-2xl font-bold text-green-600">{previewDays}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Formulario en grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Fecha de inicio */}
              <div className="space-y-3">
                <Label htmlFor="startDate" className="text-sm font-medium text-gray-700">
                  Fecha de Inicio de Días Progresivos *
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={dateToChileanString(getCurrentChileanDate())}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 leading-relaxed">
                  Fecha desde la cual se calcula automáticamente cada 3 años. 
                  El primer día progresivo se otorga inmediatamente.
                </p>
              </div>

              {/* Fecha de aprobación */}
              <div className="space-y-3">
                <Label htmlFor="approvalDate" className="text-sm font-medium text-gray-700">
                  Fecha de Aprobación (Opcional)
                </Label>
                <Input
                  id="approvalDate"
                  type="date"
                  value={approvalDate}
                  onChange={(e) => setApprovalDate(e.target.value)}
                  max={dateToChileanString(getCurrentChileanDate())}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 leading-relaxed">
                  Fecha desde la cual los días progresivos se suman al total de vacaciones disponibles. 
                  Si no se especifica, no se sumarán al total.
                </p>
              </div>
            </div>
            
            {/* Observaciones */}
            <div className="space-y-3">
              <Label htmlFor="reason" className="text-sm font-medium text-gray-700">Observaciones (Opcional)</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Notas sobre la configuración de días progresivos..."
                rows={3}
                className="w-full resize-none"
              />
            </div>

            {/* Vista previa del cálculo */}
            {startDate && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Vista Previa del Cálculo
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-green-700">Fecha de inicio:</span>
                      <span className="font-medium text-green-900">
                        {new Date(chileanInputDateToISO(startDate)).toLocaleDateString('es-CL')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Primer día progresivo:</span>
                      <span className="font-medium text-green-900">Inmediato</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Días progresivos actuales:</span>
                      <span className="font-bold text-green-900 text-lg">{previewDays}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {approvalDate ? (
                      <div className="flex justify-between">
                        <span className="text-green-700">Se suman al total desde:</span>
                        <span className="font-medium text-green-900">
                          {new Date(chileanInputDateToISO(approvalDate)).toLocaleDateString('es-CL')}
                        </span>
                      </div>
                    ) : (
                      <div className="bg-amber-100 border border-amber-300 rounded-md p-2">
                        <span className="text-amber-800 text-xs flex items-center gap-1">
                          ⚠️ Sin fecha de aprobación, no se suman al total
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Botones de acción */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t border-gray-200">
              <Button 
                type="button" 
                variant="outline" 
                onClick={resetDates}
                className="w-full sm:w-auto"
              >
                Limpiar Fechas
              </Button>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={handleClear}
                  className="w-full sm:w-auto"
                >
                  Eliminar Configuración
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => onOpenChange(false)}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={!startDate}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                >
                  Guardar Configuración
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
